/**
 * Render-side feel values: camera smoothing, facing blend, the landing
 * spring, pose springs, and the headband ribbons. Exposed in the tuning panel
 * under their own folder, next to the simulation's tuning.
 */

export interface RenderFeel {
  /** Camera look-ahead at max run speed, m. */
  cameraLookAhead: number;
  /** Horizontal smoothing half-life, ms. */
  cameraHalfLifeX: number;
  /** Vertical smoothing half-life, ms. */
  cameraHalfLifeY: number;
  /** Vertical dead band, m (total height). */
  cameraDeadBandY: number;
  /** Width of the view in meters at any window size. */
  viewWidth: number;
  /** Width of each player's half in a split-screen race, m (the halves are wide and short, so the scale matters more there). */
  viewWidthSplit: number;
  /**
   * Width of a quarter of the screen, m: a three or four player race puts players in cells half the
   * width of a half, so the view narrows to keep the body the size a player can read.
   */
  viewWidthQuad: number;
  /**
   * Meters the camera sits above the body. A full screen is tall enough to hold what is overhead
   * without one, so this is 0; a split-screen half is a third as tall and the ground under the feet
   * is dead space, so the camera rises there and the bars come into view (the owner, 2026-09-20:
   * "it's kind of hard to see the top of the bars").
   */
  cameraRise: number;
  cameraRiseSplit: number;
  /**
   * A quarter cell is taller in metres than a half (the view is narrower, so the same pixels hold
   * more world), so it needs a bigger rise to put the feet at the same place down the cell.
   */
  cameraRiseQuad: number;
  /** Facing blend time, ms. */
  facingBlendMs: number;
  /** Landing absorb spring: stiffness (1/s^2) and damping ratio. */
  landingStiffness: number;
  landingDamping: number;
  /** How much the absorb compresses per m/s of impact, m per (m/s). */
  landingPerSpeed: number;
  /** Max absorb, m. */
  landingMax: number;
  /**
   * The speed lines drawn when a move gives the body speed it could not run at (`boost.ts`): how
   * long they last, how long each one is, how far it falls behind, its thickness, and its opacity.
   * The camera does not move for them: the owner tried a pull-back on 2026-09-20 and it "feels like
   * a jump", so the cue is the lines alone.
   */
  boostMs: number;
  boostLineLength: number;
  boostLineDrift: number;
  boostLineWidth: number;
  boostAlpha: number;
  /** Lean from horizontal acceleration, rad per (m/s^2). */
  leanPerAccel: number;
  /** Max lean from acceleration, rad. */
  leanMax: number;
  /** Acceleration lean spring: natural frequency (Hz) and damping ratio (below 1 overshoots). */
  leanHz: number;
  leanDamping: number;
  /** Ground torso lean easing half-life and the body-axis blend onto the pendulum at a catch, ms. */
  bodyAngleHalfLife: number;
  /**
   * Whole-body axis spring off the bar, Hz (critically damped). It tracks the sim's attitude and its
   * rate, so it only smooths the handoffs between the ground, the air, and the bar.
   */
  bodyAngleHz: number;
  /** Pose spring natural frequencies per joint group, Hz. Critically damped. */
  springLegsHz: number;
  springArmsHz: number;
  springTorsoHz: number;
  springHeadHz: number;
  /** IK target easing half-life, ms. */
  ikHalfLife: number;
  /** Half-life of the gait / brake / idle blend that places the feet, ms (a stop or a start eases in). */
  legBlendHalfLife: number;
  /** Fraction of full reach where leg / arm IK starts to saturate (1 = hard, rigid joints). */
  ikSoftLegs: number;
  ikSoftArms: number;
  /** How far a forearm or shin may stretch or shrink to stay in contact, fraction of its length. */
  limbStretch: number;
  /** Stride length (one full cycle of a foot, two steps) at rest and gain with speed, m and m per (m/s). */
  strideBase: number;
  stridePerSpeed: number;
  /** Fraction of the cycle a foot stays planted: stanceA / speed + stanceB, clamped to [stanceSprint, stanceWalk]. */
  stanceA: number;
  stanceB: number;
  stanceWalk: number;
  stanceSprint: number;
  /** Hip bounce amplitude at max speed, m (lowest at mid-stance, highest in flight). */
  gaitBounce: number;
  /** Knee lift: foot height at the top of the swing at walking speed and the gain to max speed, m. */
  gaitLiftBase: number;
  gaitLift: number;
  /** Arm swing at the shoulder at walking speed and the gain with speed, degrees. */
  armSwingBase: number;
  armSwingPerSpeed: number;
  /** Center of the arm swing relative to the torso axis, degrees (negative is behind) and its gain with speed. The torso leans forward, so the swing has to sit behind the axis for the hands to pass the body line. */
  armCenter: number;
  armCenterPerSpeed: number;
  /** Elbow flexion at rest and the extra flexion when the arm is forward, degrees. */
  elbowBase: number;
  elbowSwing: number;
  /** Extra forward torso lean at max speed, degrees. */
  runLean: number;
  /** Speed at which the gait is fully blended in, m/s. */
  gaitBlendSpeed: number;
  /** Fraction of the stance travel that happens ahead of the hip (contact point); the rest is behind. */
  stanceAhead: number;
  /** Swing tangents at lift-off and contact as a fraction of ground speed (paw-back). */
  swingTangent: number;
  /** Knee-lift arc exponent: 1 is a sine, lower is a flatter, longer plateau. */
  liftShape: number;
  /** Arm swing lead relative to the opposite leg, fraction of a cycle. */
  armPhase: number;
  /** Fraction of the torso lean the head counters so the face stays level. */
  headLevel: number;
  /** Time warp of the swing's forward travel (q^warp): below 1 the foot passes under the hip early, so the knee drives forward while the heel is still up. */
  swingWarp: number;
  /** Torso pitch oscillation at step rate, degrees at max speed (forward at each contact). */
  gaitPitch: number;
  /** Ribbon segment count and lengths. */
  ribbonSegments: number;
  ribbonSegmentLength: number;
  ribbonGravity: number;
  ribbonDrag: number;
  ribbonStiffness: number;
  /** Ribbon acceleration per m/s of head speed, opposite to the motion. */
  ribbonWind: number;
  /** Pump flash duration, ms. */
  flashMs: number;
  /** How far the body sinks over the fail pause, m (it also fades out). */
  failSink: number;

  // Body proportions (drawing only; bone lengths are fixed in skeleton.ts)
  headRadius: number;
  /** Chest (upper torso) capsule radius and the waist (lower torso) capsule radius, m. */
  chestRadius: number;
  waistRadius: number;
  /** Chest bulge forward from the torso axis, m. */
  chestForward: number;
  armRadius: number;
  forearmRadius: number;
  thighRadius: number;
  shinRadius: number;
  /** Fist radius, m. */
  handRadius: number;
  /** Boot length ahead of the ankle and behind it (heel), and its radius, m. */
  footLength: number;
  footHeel: number;
  footRadius: number;
  sashWidth: number;
  /** Sash tail length (segments of ribbonSegmentLength). */
  sashTailSegments: number;
  /** Headband width, m, and how far above the head center it sits, m. */
  bandWidth: number;
  bandUp: number;
  ribbonWidth: number;
  /** Torso squash on landing: extra radius per meter of absorb. */
  landingSquash: number;
  /** Slide contact: how far ahead of the hip the lead foot lies on the ground, where the tucked foot sits, and how far behind the hip the trailing hand rests, m. */
  slideLeadReach: number;
  slideTuckReach: number;
  slideHandBehind: number;
  /** How far the drawn mass rises when the body is upside down on the ground (a roll), m. */
  rollLift: number;

  // Popups (LINK x N, PERFECT, SAVE)
  /** Slam-in time, hold time, and fade-out time, ms. */
  popupInMs: number;
  popupHoldMs: number;
  popupOutMs: number;
  /** Type height at scale 1, pixels at a 1080 px tall screen. */
  popupSize: number;
  popupScale: number;
  /** The corner link counter: type height and distance from the right edge and from the top, pixels at a 1080 px tall screen (the top clears the HUD's perf meter). */
  linkSize: number;
  linkMargin: number;
  linkTop: number;
  /** How long the counter stays after a run ends, ms (then it fades over popupOutMs). */
  linkHoldMs: number;
  /** The counter's pulse when the run grows: length, ms, and extra scale at its start. */
  linkPulseMs: number;
  linkPulseScale: number;
  /** Height of the first popup above the head, m, and the rise of each stacked one, in type heights. */
  popupAbove: number;
  popupStack: number;

  // Race
  /** Opacity of the other players drawn as ghosts in a split-screen half, 0..1. */
  ghostAlpha: number;

  // The eighties look: the night behind the world, the lit edges of the world, the ninja's halo
  /** Where the horizon sits, as a fraction of the viewport's height from the top, when the camera is at y 0. */
  horizonFrac: number;
  /** How much of the camera's motion the horizon, the ridge, the sun, and the stars follow (0 fixed, 1 with the world). */
  horizonParallax: number;
  ridgeParallax: number;
  sunParallax: number;
  starParallax: number;
  /** The sun's radius as a fraction of the viewport's height, and where it sits across the width at camera x 0. */
  sunRadius: number;
  sunX: number;
  /** The floor grid under the horizon: line spacing in pixels at a 1080 px tall viewport and its opacity (0 hides it). */
  floorGridPx: number;
  floorGridAlpha: number;
  /** The lit top edge of every block, m, and the glow band below it, m. */
  edgeWidth: number;
  edgeGlow: number;
  /** The grid drawn on the faces of the blocks: line spacing, m (0 hides it), and opacity. */
  gridStep: number;
  gridAlpha: number;
  /** The water's surface pulse period, ms (0 keeps it still). */
  waterPulseMs: number;
  /** The dark rim around the whole body, m (0 for none): it keeps the paper body legible over the sun and the lit edges. */
  outline: number;
  /** The halo behind the body in the headband color: radius, m (0 hides it), and opacity. */
  bodyGlow: number;
  bodyGlowAlpha: number;
  /** The mark on the ground under the feet, in the headband color: opacity at contact (0 hides it) and the height above which it is gone, m. */
  contactAlpha: number;
  contactFade: number;
}

export const defaultFeel: RenderFeel = {
  cameraLookAhead: 2.5,
  cameraHalfLifeX: 80,
  cameraHalfLifeY: 150,
  cameraDeadBandY: 1.0,
  viewWidth: 24,
  viewWidthSplit: 24,
  viewWidthQuad: 16,
  cameraRise: 0,
  cameraRiseSplit: 1.5,
  cameraRiseQuad: 2,
  boostMs: 420,
  boostLineLength: 1.6,
  boostLineDrift: 3.2,
  boostLineWidth: 0.075,
  boostAlpha: 0.9,
  facingBlendMs: 60,
  landingStiffness: 400,
  landingDamping: 1.0,
  landingPerSpeed: 0.02,
  landingMax: 0.25,
  leanPerAccel: 0.006,
  leanMax: 0.2,
  leanHz: 2.2,
  leanDamping: 0.45,
  bodyAngleHalfLife: 35,
  bodyAngleHz: 5,
  springLegsHz: 6,
  springArmsHz: 6,
  springTorsoHz: 8,
  springHeadHz: 5,
  ikHalfLife: 25,
  legBlendHalfLife: 45,
  ikSoftLegs: 0.9,
  ikSoftArms: 0.97,
  limbStretch: 0.12,
  strideBase: 1.3,
  stridePerSpeed: 0.34,
  stanceA: 0.81,
  stanceB: 0.06,
  stanceWalk: 0.6,
  stanceSprint: 0.15,
  gaitBounce: 0.05,
  gaitLiftBase: 0.12,
  gaitLift: 0.35,
  armSwingBase: 30,
  armSwingPerSpeed: 35,
  armCenter: -8,
  armCenterPerSpeed: -16,
  elbowBase: 38,
  elbowSwing: 68,
  runLean: 12,
  gaitBlendSpeed: 2.5,
  stanceAhead: 0.25,
  swingTangent: 0.6,
  liftShape: 0.7,
  armPhase: 0.08,
  headLevel: 0.7,
  swingWarp: 0.72,
  gaitPitch: 2,
  ribbonSegments: 9,
  ribbonSegmentLength: 0.07,
  ribbonGravity: 9,
  ribbonDrag: 3,
  ribbonStiffness: 2,
  ribbonWind: 1.5,
  flashMs: 120,
  failSink: 0.7,

  headRadius: 0.135,
  chestRadius: 0.16,
  waistRadius: 0.105,
  chestForward: 0.03,
  armRadius: 0.07,
  forearmRadius: 0.06,
  thighRadius: 0.09,
  shinRadius: 0.072,
  handRadius: 0.065,
  footLength: 0.2,
  footHeel: 0.05,
  footRadius: 0.052,
  sashWidth: 0.075,
  sashTailSegments: 3,
  bandWidth: 0.08,
  bandUp: 0.025,
  ribbonWidth: 0.05,
  landingSquash: 0.2,
  slideLeadReach: 0.82,
  slideTuckReach: 0.08,
  slideHandBehind: 0.25,
  rollLift: 0.2,

  popupInMs: 60,
  popupHoldMs: 500,
  popupOutMs: 200,
  popupSize: 44,
  popupScale: 1,
  linkSize: 34,
  linkMargin: 36,
  linkTop: 190,
  linkHoldMs: 1500,
  linkPulseMs: 180,
  linkPulseScale: 0.15,
  popupAbove: 0.6,
  popupStack: 1.05,

  ghostAlpha: 0.4,

  horizonFrac: 0.5,
  horizonParallax: 0.35,
  ridgeParallax: 0.12,
  sunParallax: 0.01,
  starParallax: 0.04,
  sunRadius: 0.14,
  sunX: 0.8,
  floorGridPx: 96,
  floorGridAlpha: 0,
  edgeWidth: 0.06,
  edgeGlow: 0.2,
  gridStep: 1,
  gridAlpha: 0.1,
  waterPulseMs: 2400,
  outline: 0.028,
  bodyGlow: 0.9,
  bodyGlowAlpha: 0.16,
  contactAlpha: 0.4,
  contactFade: 3,
};

export const FEEL_KEYS = Object.keys(defaultFeel) as (keyof RenderFeel)[];

export const FEEL_GROUPS: Record<string, (keyof RenderFeel)[]> = {
  camera: [
    "cameraLookAhead",
    "cameraHalfLifeX",
    "cameraHalfLifeY",
    "cameraDeadBandY",
    "viewWidth",
    "viewWidthSplit",
    "viewWidthQuad",
    "cameraRise",
    "cameraRiseSplit",
    "cameraRiseQuad",
  ],
  body: [
    "facingBlendMs",
    "landingStiffness",
    "landingDamping",
    "landingPerSpeed",
    "landingMax",
    "leanPerAccel",
    "leanMax",
    "leanHz",
    "leanDamping",
    "bodyAngleHalfLife",
    "bodyAngleHz",
    "rollLift",
  ],
  pose: [
    "springLegsHz",
    "springArmsHz",
    "springTorsoHz",
    "springHeadHz",
    "ikHalfLife",
    "legBlendHalfLife",
    "ikSoftLegs",
    "ikSoftArms",
    "limbStretch",
  ],
  gait: [
    "strideBase",
    "stridePerSpeed",
    "stanceA",
    "stanceB",
    "stanceWalk",
    "stanceSprint",
    "gaitBounce",
    "gaitLiftBase",
    "gaitLift",
    "armSwingBase",
    "armSwingPerSpeed",
    "armCenter",
    "armCenterPerSpeed",
    "elbowBase",
    "elbowSwing",
    "runLean",
    "gaitBlendSpeed",
    "stanceAhead",
    "swingTangent",
    "liftShape",
    "armPhase",
    "headLevel",
    "swingWarp",
    "gaitPitch",
  ],
  proportions: [
    "headRadius",
    "chestRadius",
    "waistRadius",
    "chestForward",
    "armRadius",
    "forearmRadius",
    "thighRadius",
    "shinRadius",
    "handRadius",
    "footLength",
    "footHeel",
    "footRadius",
    "sashWidth",
    "sashTailSegments",
    "bandWidth",
    "bandUp",
    "ribbonWidth",
    "landingSquash",
    "slideLeadReach",
    "slideTuckReach",
    "slideHandBehind",
  ],
  popups: [
    "popupInMs",
    "popupHoldMs",
    "popupOutMs",
    "popupSize",
    "popupScale",
    "linkSize",
    "linkMargin",
    "linkTop",
    "linkHoldMs",
    "linkPulseMs",
    "linkPulseScale",
    "popupAbove",
    "popupStack",
  ],
  ribbon: [
    "ribbonSegments",
    "ribbonSegmentLength",
    "ribbonGravity",
    "ribbonDrag",
    "ribbonStiffness",
    "ribbonWind",
    "flashMs",
    "failSink",
  ],
  race: ["ghostAlpha"],
  boost: ["boostMs", "boostLineLength", "boostLineDrift", "boostLineWidth", "boostAlpha"],
  retro: [
    "horizonFrac",
    "horizonParallax",
    "ridgeParallax",
    "sunParallax",
    "starParallax",
    "sunRadius",
    "sunX",
    "floorGridPx",
    "floorGridAlpha",
    "edgeWidth",
    "edgeGlow",
    "gridStep",
    "gridAlpha",
    "waterPulseMs",
    "outline",
    "bodyGlow",
    "bodyGlowAlpha",
    "contactAlpha",
    "contactFade",
  ],
};

export function mergeFeel(base: RenderFeel, partial: Partial<RenderFeel> | null | undefined): RenderFeel {
  const out = { ...base };
  if (!partial) return out;
  for (const k of FEEL_KEYS) {
    const v = partial[k];
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

/** Exponential smoothing factor for a half-life in ms over a step of dt seconds. */
export function halfLifeFactor(halfLifeMs: number, dt: number): number {
  if (halfLifeMs <= 0) return 1;
  return 1 - 0.5 ** ((dt * 1000) / halfLifeMs);
}
